import {
  Box,
  Center,
  Spinner,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import {
  OrganizationPartnerItemFragment,
  PartnerPreferenceType,
  PartnerType,
} from '@client/graphql/__generated__/types';
import { memo, useCallback, useState } from 'react';
import { useRosterPreferenceMutation } from './hooks';
import { RosterTableRow } from './RosterTableRow';
import { RosterEntry } from './types';

interface RosterTableProps {
  entries: RosterEntry[];
  partners: OrganizationPartnerItemFragment[];
  loading: boolean;
}

const headerStyles = {
  bg: 'transparent',
  border: 'none',
  borderBottom: '1px solid',
  borderColor: 'whiteAlpha.100',
  color: 'whiteAlpha.600',
  fontSize: '11px',
  fontWeight: 'medium',
  letterSpacing: '0.88px',
  py: 4,
  px: 5,
  textTransform: 'uppercase' as const,
};

export const RosterTable = memo(function RosterTable({
  entries,
  partners,
  loading,
}: RosterTableProps) {
  const toast = useToast();
  const { updatePartnerPreference, updateContactable } =
    useRosterPreferenceMutation();
  const [updatingRowId, setUpdatingRowId] = useState<string | null>(null);

  const handleUpdatePreference = useCallback(
    (
      rosterEntryId: string,
      partnerType: PartnerType,
      preferenceType: PartnerPreferenceType,
      partnerId?: string | null,
      partnerContactId?: string | null
    ) => {
      setUpdatingRowId(rosterEntryId);
      void updatePartnerPreference({
        rosterEntryId,
        partnerType,
        preferenceType,
        partnerId,
        partnerContactId,
      })
        .catch(() => {
          toast({
            title: 'Error',
            description: 'Failed to update preference',
            status: 'error',
            duration: 3_000,
          });
        })
        .finally(() => {
          setUpdatingRowId(null);
        });
    },
    [toast, updatePartnerPreference]
  );

  const handleUpdateContactable = useCallback(
    (rosterEntryId: string, isContactable: boolean) => {
      setUpdatingRowId(rosterEntryId);
      void updateContactable(rosterEntryId, isContactable)
        .catch(() => {
          toast({
            title: 'Error',
            description: 'Failed to update contactable status',
            status: 'error',
            duration: 3_000,
          });
        })
        .finally(() => {
          setUpdatingRowId(null);
        });
    },
    [toast, updateContactable]
  );

  if (loading) {
    return (
      <Center py={10}>
        <Spinner color="whiteAlpha.600" />
      </Center>
    );
  }

  if (entries.length === 0) {
    return (
      <Center py={10}>
        <Text color="whiteAlpha.600">No agents in roster</Text>
      </Center>
    );
  }

  return (
    <Box
      bg="whiteAlpha.50"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderRadius="10px"
      overflowX="auto"
    >
      <Table variant="unstyled">
        <Thead>
          <Tr>
            <Th {...headerStyles} minWidth="200px">
              Agent
            </Th>
            <Th {...headerStyles} minWidth="200px">
              Preferred Lender
            </Th>
            <Th {...headerStyles} minWidth="200px">
              Preferred Title
            </Th>
            <Th {...headerStyles} minWidth="200px">
              Preferred Insurance
            </Th>
            <Th {...headerStyles} width="140px">
              Annual Transactions
            </Th>
            <Th {...headerStyles} width="100px">
              Contactable
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {entries.map((entry) => (
            <RosterTableRow
              key={entry.id}
              entry={entry}
              isUpdating={updatingRowId === entry.id}
              partners={partners}
              onUpdateContactable={handleUpdateContactable}
              onUpdatePreference={handleUpdatePreference}
            />
          ))}
        </Tbody>
      </Table>
    </Box>
  );
});
